import { socialNetworkTypeIdToChartKey } from "./messagesReport"
import { buildPaidPerformanceFetchKey, formatPaidPerformanceDate } from "./paidPerformanceReport"
import { normalizeDashboardPlatformKey } from "./socialPerformanceReport"
import { socialNetworkTypeIdToDashboardPlatformKey } from "./socialNetworkCatalog"

export function formatPaidMediaPerformanceDate(date) {
  return formatPaidPerformanceDate(date)
}

export function buildPaidMediaPerformanceFetchKey(clientId, startDate, endDate) {
  return buildPaidPerformanceFetchKey(clientId, startDate, endDate)
}

function resolvePlatformKey(typeId, socialNetworkTypes) {
  const fromCatalog =
    socialNetworkTypeIdToChartKey(socialNetworkTypes, typeId) ??
    socialNetworkTypeIdToDashboardPlatformKey(typeId, socialNetworkTypes)
  return fromCatalog ? normalizeDashboardPlatformKey(fromCatalog) : null
}

function normalizeSeriesPoint(point) {
  const spend = Number(point?.ad_spend) || 0
  const revenue = Number(point?.revenue_generated) || 0
  const roasRaw = Number(point?.roas)
  return {
    date: point?.date ?? "",
    spend,
    revenue,
    roas: Number.isFinite(roasRaw) ? roasRaw : 0,
  }
}

function summarizeSeries(series) {
  const totals = (Array.isArray(series) ? series : []).reduce(
    (acc, row) => {
      acc.spend += row.spend
      acc.revenue += row.revenue
      return acc
    },
    { spend: 0, revenue: 0 },
  )
  return {
    spend: totals.spend,
    revenue: totals.revenue,
    roas: totals.spend > 0 ? totals.revenue / totals.spend : 0,
  }
}

export function unwrapPaidMediaPerformancePayload(data) {
  const rows = Array.isArray(data?.data) ? data.data : []
  const summary = data?.summary ?? {}
  return {
    clientId: data?.client_id ?? data?.clientId ?? null,
    startDate: data?.start_date ?? data?.startDate ?? null,
    endDate: data?.end_date ?? data?.endDate ?? null,
    platformRows: rows,
    summary: {
      totalSpend: Number(summary.total_spend ?? summary.totalSpend) || 0,
      revenueGenerated: Number(summary.revenue_generated ?? summary.revenueGenerated) || 0,
      averageRoas: Number(summary.average_roas ?? summary.averageRoas) || 0,
      revenueChangePercent: Number(
        summary.revenue_change_percent ?? summary.revenueChangePercent,
      ),
    },
  }
}

/**
 * Chart-ready paid media performance (line series + summary cards).
 */
export function buildPaidMediaChartView(payload, socialNetworkTypes = []) {
  const rows = payload?.platformRows ?? []
  const summary = payload?.summary ?? {}

  const seriesByPlatform = {}
  let aggregateSeries = null

  for (const row of rows) {
    const typeId = row?.social_network_type_id ?? row?.socialNetworkTypeId
    const platformKey = resolvePlatformKey(typeId, socialNetworkTypes)
    const series = (row?.series ?? []).map(normalizeSeriesPoint)

    if (platformKey === "all") {
      aggregateSeries = series
      continue
    }
    if (platformKey) {
      seriesByPlatform[platformKey] = series
    }
  }

  const series = aggregateSeries?.length ? aggregateSeries : []

  const totalsFromSummary = {
    spend: summary.totalSpend ?? 0,
    revenue: summary.revenueGenerated ?? 0,
    roas: summary.averageRoas ?? 0,
  }

  const totalsByPlatform = Object.fromEntries(
    Object.entries(seriesByPlatform).map(([key, platSeries]) => [
      key,
      summarizeSeries(platSeries),
    ]),
  )

  return {
    series,
    seriesByPlatform,
    totals: totalsFromSummary,
    totalsByPlatform,
    revenueChangePercent: Number.isFinite(summary.revenueChangePercent)
      ? summary.revenueChangePercent
      : null,
  }
}

export function getPaidMediaSeriesForPlatform(chartView, platformKey) {
  if (!chartView) return []
  if (platformKey === "all") return chartView.series ?? []
  const key = normalizeDashboardPlatformKey(platformKey)
  return chartView.seriesByPlatform?.[key] ?? []
}

export function getPaidMediaTotalsForPlatform(chartView, platformKey) {
  const empty = { spend: 0, revenue: 0, roas: 0 }
  if (!chartView) return empty
  if (platformKey === "all") return chartView.totals ?? empty
  const key = normalizeDashboardPlatformKey(platformKey)
  return chartView.totalsByPlatform?.[key] ?? empty
}
